import { API } from 'aws-amplify';

const API_NAME = 'EFWAPI';

/** Respostas de validateToken que significam token inválido, expirado ou sem permissão de dono da plataforma. */
const SESSION_ERRORS = ['token_not_found', 'not_allowed', 'inactive'];

const RESULT_MESSAGES = {
    not_found: 'Registro não encontrado.',
    referenced_by_stores: 'Este tenant ainda possui lojas cadastradas e não pode ser apagado.',
    referenced_by_users: 'Este tenant ainda possui usuários cadastrados e não pode ser apagado.',
    external_id_duplicated: 'Já existe outro tenant com este ID externo.',
    store_active: 'Desative a loja antes de apagar os dados.',
    operation_not_allowed: 'Operação não permitida.',
    forbidden: 'Operação não permitida.',
    fatal_error: 'Ocorreu um erro na aplicação.'
};

const networkError = (status) => ({
    success: false,
    result: 'network_error',
    http_code: status || 0,
    error: { details: [{ message: 'Falha de comunicação com o servidor. Tente novamente.' }] }
});

export const isSessionError = (response) => response?.http_code === 401 || SESSION_ERRORS.includes(response?.result);

export const errorMessage = (response) => {
    const details = response?.error?.details;
    if (Array.isArray(details) && details.length) return details.map((d) => d.message).join(' ');
    return RESULT_MESSAGES[response?.result] || 'Não foi possível concluir a operação.';
};

/**
 * Não fixar Content-Type: o Amplify envia corpo "" em DELETE/GET e, declarado como JSON, o bodyParser estrito da API responde 400.
 * Quando há body, o próprio Amplify declara application/json.
 */
const request = async (method, path, token, body, query) => {
    const init = { headers: {} };
    if (token) init.headers.Authorization = `Bearer ${token}`;
    if (body !== undefined) init.body = body;
    if (query) {
        init.queryStringParameters = Object.fromEntries(
            Object.entries(query).filter(([, value]) => value !== undefined && value !== null && value !== '')
        );
    }

    try {
        return await API[method](API_NAME, path, init);
    } catch (err) {
        const data = err?.response?.data;
        if (data && typeof data === 'object') return { http_code: err.response.status, ...data };
        return networkError(err?.response?.status);
    }
};

export const adminLogin = (email, password) => request('post', '/api/user/login', null, { email, password });

export const createAdminApi = (token) => ({
    logout: () => request('post', '/api/user/logout', token, {}),
    overview: () => request('get', '/api/admin/overview', token),
    listStores: (filters) => request('get', '/api/admin/stores', token, undefined, filters),
    listUsers: (filters) => request('get', '/api/admin/users', token, undefined, filters),
    options: () => request('get', '/api/admin/options', token),
    myProfile: () => request('get', '/api/admin/me', token),
    updateMyProfile: (data) => request('post', '/api/admin/me', token, data),
    tenantDetail: (tenantId) => request('get', `/api/admin/tenants/${tenantId}`, token),
    createTenant: (data) => request('post', '/api/tenants', token, data),
    renameTenant: (tenantId, name) => request('post', `/api/admin/tenants/${tenantId}/rename`, token, { name }),
    deactivateTenant: (tenantId) => request('post', `/api/admin/tenants/${tenantId}/deactivate`, token, {}),
    activateTenant: (tenantId) => request('post', `/api/admin/tenants/${tenantId}/activate`, token, {}),
    deleteTenant: (tenantId) => request('del', `/api/tenants/${tenantId}`, token),
    purgeTenant: (tenantId, confirmName) => request('post', `/api/admin/tenants/${tenantId}/purge`, token, { confirm_name: confirmName }),
    recoverAccess: (tenantId) => request('post', `/api/tenants/${tenantId}/users/recover-access`, token, {}),

    deactivateStore: (tenantId, externalStoreId) =>
        request('post', `/api/tenants/${tenantId}/stores/external/${externalStoreId}/deactivate`, token, {}),
    activateStore: (tenantId, externalStoreId) =>
        request('post', `/api/tenants/${tenantId}/stores/external/${externalStoreId}/activate`, token, {}),
    purgeStoreData: (tenantId, externalStoreId, confirmCnpj) =>
        request('post', `/api/tenants/${tenantId}/stores/external/${externalStoreId}/purge-data`, token, { confirm_cnpj: confirmCnpj }),
    purgeAccountPayable: (tenantId, storeId, filter) =>
        request('post', `/api/admin/tenants/${tenantId}/stores/${storeId}/account-payable/purge`, token, filter),
    makeMainStore: (tenantId, storeId) => request('post', `/api/admin/tenants/${tenantId}/stores/${storeId}/make-main`, token, {}),
    deleteStore: (tenantId, storeId, confirm) =>
        request('post', `/api/admin/tenants/${tenantId}/stores/${storeId}/delete`, token, { confirm }),
    resetStoreCredentials: (tenantId, storeId) =>
        request('post', `/api/tenants/${tenantId}/stores/${storeId}/reset-credentials`, token, {}),

    deactivateUser: (tenantId, userId) => request('post', `/api/admin/tenants/${tenantId}/users/${userId}/deactivate`, token, {}),
    activateUser: (tenantId, userId) => request('post', `/api/admin/tenants/${tenantId}/users/${userId}/activate`, token, {}),
    setUserPassword: (tenantId, userId, password) =>
        request('post', `/api/tenants/${tenantId}/users/${userId}/set-password`, token, { password }),
    deleteUser: (tenantId, userId) => request('del', `/api/tenants/${tenantId}/users/${userId}`, token),
    promoteToSaas: (tenantId, userId, confirmEmail) =>
        request('post', `/api/admin/tenants/${tenantId}/users/${userId}/promote-saas`, token, { confirm_email: confirmEmail }),
    demoteToTenant: (userId, tenantId, confirmEmail) =>
        request('post', `/api/admin/platform-users/${userId}/demote`, token, { tenant_id: tenantId, confirm_email: confirmEmail })
});
